import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import logoHead from "../images/Logo_head_white.png";
import axios from 'axios';
import '../components/FeedbackForm/FeedbackForm.css'; 

const colors = {
  mainBlue: "#061941",
  cafeNoir: "#583E23",
  carrieban: "#3C6E71",
  isabelline: "#F4F3EE",
  gold: "#F2AF29"
};

function FadeInFromLeft({ children, delay = 0 }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setVisible(true), delay);
          observer.disconnect();
        }
      },
      { threshold: 0.19 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [delay]);

  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "none" : "translateX(-36px)",
        transition: "opacity 0.8s, transform 0.8s cubic-bezier(.61,.09,.66,1.2)",
        willChange: "opacity, transform"
      }}
    >
      {children}
    </div>
  );
}

export default function FeedbackForm() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    userType: "",
    rating: "",
    category: "",
    feedback: "",
    suggestions: ""
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/feedback`, 
        formData,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      
      setSubmitted(true);
      setFormData({
        name: "",
        email: "",
        userType: "",
        rating: "",
        category: "",
        feedback: "",
        suggestions: ""
      });
      
      setTimeout(() => {
        setSubmitted(false);
        setIsSubmitting(false);
      }, 4000);
      
    } catch (err) {
      console.error(err);
      alert('Failed to submit feedback. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="feedback-container">
      {/* HEADER */}
      <header className="feedback-header">
        <img
          src={logoHead}
          alt="NeuHawk logo"
          className="logo"
        />
        <h1 className="main-title">
          NeuHawk Feedback
        </h1>
      </header>

      {/* MAIN CONTENT */}
      <main className="feedback-main">
        <FadeInFromLeft>
          <div className="feedback-intro">
            <h2 className="feedback-title">
              Help us improve NeuHawk
            </h2>
            <p className="feedback-subtitle">
              Your feedback shapes the future of graduate student networking. 
              Tell us about your experience and help us build something amazing together.
            </p>
          </div>
        </FadeInFromLeft>

        <FadeInFromLeft delay={200}>
          <div className="feedback-form-container">
            {submitted ? (
              <div className="success-message">
                <div className="success-icon">✨</div>
                <h3>Thank you for your feedback!</h3>
                <p>Your input helps us make NeuHawk better for everyone.</p>
                <button 
                  className="back-button"
                  onClick={() => {
                    const userData = JSON.parse(localStorage.getItem('user'));
                    if (userData && userData.role === 'professor') {
                      navigate('/professor-dashboard');
                    } else if (userData && userData.role === 'student') {
                      navigate('/student-dashboard');
                    } else if (userData && userData.role === 'admin') {
                      navigate('/admin');
                    } else {
                      navigate('/HomePage');
                    }
                  }}
                >
                  Back to NeuHawk
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="feedback-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your full name"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your.email@university.edu"
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>I am a...</label>
                    <select
                      name="userType"
                      value={formData.userType}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select your role</option>
                      <option value="graduate-student">Graduate Student</option>
                      <option value="faculty">Faculty Member</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Overall Rating</label>
                    <select
                      name="rating"
                      value={formData.rating}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Rate your experience</option>
                      <option value="5">⭐⭐⭐⭐⭐ Excellent</option>
                      <option value="4">⭐⭐⭐⭐ Good</option>
                      <option value="3">⭐⭐⭐ Average</option>
                      <option value="2">⭐⭐ Poor</option>
                      <option value="1">⭐ Very Poor</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Feedback Category</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">What is this feedback about?</option>
                    <option value="matching-system">Matching System</option>
                    <option value="user-interface">User Interface</option>
                    <option value="messaging">Messaging & Communication</option>
                    <option value="profile-features">Profile Features</option>
                    <option value="bug-report">Bug Report</option>
                    <option value="feature-request">Feature Request</option>
                    <option value="general">General Feedback</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Your Feedback</label>
                  <textarea
                    name="feedback"
                    value={formData.feedback}
                    onChange={handleInputChange}
                    placeholder="Tell us about your experience with NeuHawk. What worked well? What didn't? Be specific about any issues you encountered."
                    rows="5"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Suggestions for Improvement (Optional)</label>
                  <textarea
                    name="suggestions"
                    value={formData.suggestions}
                    onChange={handleInputChange}
                    placeholder="How can we make NeuHawk better? Any new features or improvements you'd like to see?"
                    rows="4"
                  />
                </div>

                <div className="form-actions">
                  <button 
                    type="button" 
                    className="cancel-button"
                    onClick={() => navigate("/HomePage")}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="submit-button"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Submitting..." : "Submit Feedback"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </FadeInFromLeft>
      </main>
    </div>
  );
}